import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { pairwise, startWith, Subscription } from 'rxjs';
import {
  BuildingType,
  ConstructionType,
  CoverageType,
  getBuildingTypeLabel,
  getConstructionTypeLabel,
  getCoverageTypeLabel,
  getHeatingSystemLabel,
  getRoofTypeLabel,
  getSecurityMeasureLabel,
  HeatingSystem,
  RoofType,
  SecurityMeasure,
} from 'src/app/_enums/building.enums';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import { getControl } from 'src/app/_helpers/utils';
import { FamilyMember } from 'src/app/_types/auth.types';
import { BuildingInsuranceMetatadata } from 'src/app/_types/lead.types';

import { LeadFormBase } from '../lead-form-base';

type BuildingClaimInfoGroup = {
  type: FormControl<CoverageType | null>;
  count: FormControl<number | null>;
  cost: FormControl<number | null>;
  availableValues: FormControl<CoverageType[] | null>;
};

@Component({
  selector: 'app-lead-building-form',
  templateUrl: './building-form.component.html',
  styleUrls: ['./building-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
})
export class BuildingFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  @Input()
  familyMembers!: FamilyMember[];

  @Input()
  isCompany = false;

  memberIdSubscription!: Subscription;

  buildingTypeSubscription!: Subscription;

  pastInsuredSubscription!: Subscription;

  anyClaimsSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUILDING,
    benefits: this.fb.control(null),
    buildingType: this.fb.control(null),
    buildingTypeDescription: this.fb.control(null),
    securityMeasures: this.fb.control(null),
    heatingSystem: this.fb.control(null),
    heatingSystemDescriptions: this.fb.control(null),
    sumInsured: this.fb.control(null),
    yearOfConstruction: this.fb.control(null),
    typeOfConstruction: this.fb.control(null),
    typeOfRoof: this.fb.control(null),
    sum: this.fb.control(null),
    franchise: this.fb.control(null),
    use: this.fb.control(null),
    noOfApartment: this.fb.control(null),
    noOfTrade: this.fb.control(null),
    commercialArea: this.fb.control(null),
    pastInsured: this.fb.control(null),
    companyName: this.fb.control(null),
    reason: this.fb.control(null),
    anyClaims: this.fb.control(null),
    claimDetails: this.fb.array<FormGroup<BuildingClaimInfoGroup>>([]),
    roomCount: this.fb.control(null),
    buildingAddress: this.fb.control(null),
    birthDate: this.fb.control(null),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const buildingMetadata = this.metadata as BuildingInsuranceMetatadata;
      this.metadataFormGroup.patchValue(this.metadata);
      if (buildingMetadata.claimDetails) {
        for (let i = 0; i < buildingMetadata.claimDetails.length; i += 1) {
          this.addRow();
          this.metadataFormGroup.controls['claimDetails'].patchValue(
            buildingMetadata.claimDetails
          );
        }
      }
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    if (this.formGroup.controls['memberId'].value) {
      this.setMemberInfo(this.formGroup.controls['memberId'].value);
    }

    this.memberIdSubscription = this.formGroup.controls[
      'memberId'
    ].valueChanges.subscribe((value) => {
      this.setMemberInfo(value);
    });

    this.buildingTypeSubscription = this.metadataFormGroup.controls[
      'buildingType'
    ].valueChanges.subscribe((value) => {
      if (value !== BuildingType.APPARTMENT_COMPLEX) {
        this.metadataFormGroup.controls['use'].reset();
        this.metadataFormGroup.controls['noOfApartment'].reset();
        this.metadataFormGroup.controls['commercialArea'].reset();
        this.metadataFormGroup.controls['noOfTrade'].reset();
      }
    });
    this.pastInsuredSubscription = this.metadataFormGroup.controls[
      'pastInsured'
    ].valueChanges.subscribe((value) => {
      if (value === true) {
        this.metadataFormGroup.controls['reason'].reset();
      } else if (value === false) {
        this.metadataFormGroup.controls['companyName'].reset();
      }
    });

    this.anyClaimsSubscription = this.metadataFormGroup.controls[
      'anyClaims'
    ].valueChanges.subscribe((value) => {
      if (value === false) {
        (this.metadataFormGroup.controls['claimDetails'] as FormArray).clear();
        this.globalAvailableValues = Object.values(CoverageType);
      } else if (value === true) {
        this.addRow();
      }
    });

    if (!this.isCompany) {
      this.buildingCoverageValues = this.buildingCoverageValues.filter(
        (value) =>
          value !== CoverageType.ALL_RISK &&
          value !== CoverageType.RENTAL_INCOME
      );
    }
  }

  setMemberInfo(familyMemberId: string) {
    if (!this.familyMembers || this.familyMembers.length === 0) return;
    const familyMember = this.familyMembers.find(
      (x) => x.familyMemberId === familyMemberId
    );
    this.metadataFormGroup.controls['birthDate'].setValue(
      new Date(familyMember?.dob!).toLocaleDateString('fr-FR')
    );
  }

  ngOnDestroy(): void {
    this.memberIdSubscription.unsubscribe();
    this.buildingTypeSubscription.unsubscribe();
    this.pastInsuredSubscription.unsubscribe();
    this.anyClaimsSubscription.unsubscribe();
  }

  buildingCoverageValues = Object.values(CoverageType);

  getBuildingCoverageLabel = getCoverageTypeLabel;

  BuildingType = BuildingType;

  buildingTypeValues = Object.values(BuildingType);

  getBuildingTypeLabel = getBuildingTypeLabel;

  securityMeasureValues = Object.values(SecurityMeasure);

  getSecurityMeasureLabel = getSecurityMeasureLabel;

  heatingSystemValues = Object.values(HeatingSystem);

  getHeatingsSystemLabel = getHeatingSystemLabel;

  constructionTypeValues = Object.values(ConstructionType);

  getConstructionTypeLabel = getConstructionTypeLabel;

  roofTypeValues = Object.values(RoofType);

  getRoofTypeLabel = getRoofTypeLabel;

  getControl = getControl;

  // Used to track the available values for each claim type for all rows (used to reset the available values when a value is selected and to add a new row)
  globalAvailableValues: CoverageType[] = Object.values(CoverageType);

  addRow(): void {
    if (this.globalAvailableValues.length === 0) return;
    const row = this.fb.group<BuildingClaimInfoGroup>({
      type: new FormControl(null, Validators.required),
      count: new FormControl(null, Validators.required),
      cost: new FormControl(null, Validators.required),
      availableValues: new FormControl(
        this.globalAvailableValues.map((x) => x)
      ),
    });
    // When changing the value of a claim type,
    // we remove the selected value from the available values of the other rows
    // and add the old value to the available values of the other rows
    // We do the same for the global available values
    row
      .get('type')
      ?.valueChanges.pipe(startWith(null), pairwise())
      .subscribe((valuesArray) => {
        const oldValue = valuesArray[0];
        const value = valuesArray[1];
        this.claims.controls.forEach((control) => {
          if (row !== control) {
            const controlAvailableValues =
              control.get('availableValues')!.value;
            const valueIndex = controlAvailableValues!.indexOf(value!);
            if (valueIndex !== -1)
              controlAvailableValues?.splice(valueIndex, 1);

            if (oldValue !== null) {
              this.globalAvailableValues.push(oldValue);
              controlAvailableValues!.push(oldValue);
            }
            control.get('availableValues')!.setValue(controlAvailableValues);
          }
        });
        const globalValueIndex = this.globalAvailableValues.indexOf(value!);
        if (globalValueIndex !== -1)
          this.globalAvailableValues.splice(globalValueIndex, 1);
      });
    this.claims.push(row);
  }

  removeRow(index: number): void {
    if (this.claims.length > 1) {
      const typeControl = this.getGroup(index)?.get('type') as FormControl;
      if (typeControl?.value !== null) {
        const { value } = typeControl;
        this.globalAvailableValues.push(value);
        this.claims.controls.forEach((control, i) => {
          if (index !== i) {
            const controlAvailableValues =
              control.get('availableValues')!.value;
            controlAvailableValues!.push(value);
            control.get('availableValues')?.setValue(controlAvailableValues);
          }
        });
      }
      this.claims.removeAt(index);
    }
    this.cdr.detectChanges();
  }

  getGroup(index: number): FormGroup {
    return this.claims.at(index) as FormGroup;
  }

  getAvailableValues(index: number): CoverageType[] {
    return this.getGroup(index).get('availableValues')?.value;
  }

  get addButtonDisabled(): boolean {
    return (
      this.globalAvailableValues.length === 0 ||
      this.claims.controls.some((control) => control.invalid)
    );
  }

  get canRemoveRow(): boolean {
    return this.claims.length > 1;
  }

  get claims(): FormArray<FormGroup<BuildingClaimInfoGroup>> {
    return this.metadataFormGroup.controls['claimDetails'] as FormArray<
      FormGroup<BuildingClaimInfoGroup>
    >;
  }
}
